import {
  Activity,
  AlertTriangle,
  BadgeCheck,
  BookOpen,
  CheckCircle2,
  ChevronDown,
  CircleDot,
  Columns3,
  Database,
  Download,
  Eye,
  FileArchive,
  FileJson,
  FileUp,
  Filter,
  FolderOpen,
  Gauge,
  GitMerge,
  GitCompareArrows,
  Grid3X3,
  HardDrive,
  Info,
  Keyboard,
  Layers3,
  Loader2,
  Menu,
  Moon,
  PanelRight,
  Play,
  Plus,
  Radar,
  RefreshCw,
  Save,
  Search,
  Scissors,
  Send,
  Settings,
  ShieldCheck,
  SkipBack,
  SkipForward,
  Sparkles,
  Square,
  Tags,
  UploadCloud,
  Undo2,
  Wand2,
  Wrench,
  X,
  XCircle,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { AuraTelemetryEventLog, ProoflineStatus } from "@auraone/aura-ide-kit";
import { commands, datasets, probeTrials } from "./data";
import {
  buildVirtualWindow,
  clampTime,
  defaultFilters,
  defaultSort,
  exportManifest,
  formatTime,
  nextSuccessState,
  readinessTone,
  summarizeDataset,
  visibleEpisodes,
} from "./core";
import {
  artifactPlan,
  buildDeterministicClusters,
  buildLocalEvidenceArchive,
  buildProbeReport,
  buildSensorQaReport,
  buildSensorQaMarkdown,
  createDatasetFromLocalFiles,
  downloadBinaryArtifact,
  downloadTextArtifact,
  mergeClusterRows,
  recomputeDeterministicClusters,
  refreshClusterRows,
  splitClusterRows,
  summarizeClusterRows,
  type ExportScope,
  type LocalFileEntry,
} from "./localOperations";
import {
  LocalDiagnosticEventBuffer,
  ROBOTICS_PLATFORM_HOOKS,
  createRoboticsDiagnosticEvent,
  ensureRoboticsIntakeInstallSigningKeypair,
  roboticsIntakeInstallSigningKeypairKey,
  toAuraTelemetryEvents,
  type LocalDiagnosticEntry,
} from "./platformContracts";
import type {
  Cluster,
  CommandId,
  DatasetTab,
  Density,
  Episode,
  ExportTarget,
  FilterState,
  Intervention,
  SensorStream,
  SortField,
  SortState,
  ViewMode,
} from "./types";
import type { OperationState, UpdateState } from "./types";

import {
  Chip,
  DatasetProvenanceNote,
  RoboticsStudioLogo,
  appVersion,
  useDialogFocusTrap,
} from "./components";
export const exportTargets: ExportTarget[] = [
  "HF Hub",
  "Local disk",
  "Embodiment card",
  "Failure gallery",
  "AuraOne Programs",
];

import { BrowseView, EpisodeCard, FilterControls, SensorToggles, SettingsView, ViewContext } from "./views-browse";
import { ScrubView } from "./views-run";
import { ClusterView, CompareView, ProbeView, QaView } from "./views-analysis";
function ExportView({
  dataset,
  filteredEpisodes,
  exportTarget,
  setExportTarget,
  manifest,
  clusterRows,
  recordDiagnostic,
}: Pick<
  Parameters<typeof ViewBody>[0],
  | "dataset"
  | "filteredEpisodes"
  | "exportTarget"
  | "setExportTarget"
  | "manifest"
  | "clusterRows"
  | "recordDiagnostic"
>) {
  const summary = summarizeDataset(dataset);
  const [expandedStep, setExpandedStep] = useState<
    "source" | "destination" | "scope" | "artifacts"
  >("artifacts");
  const [exportState, setExportState] = useState<OperationState>("idle");
  const [exportProgress, setExportProgress] = useState(0);
  const [includeInterventions, setIncludeInterventions] = useState(true);
  const [includeSensorQa, setIncludeSensorQa] = useState(true);
  const [includeEmbodimentCard, setIncludeEmbodimentCard] = useState(true);
  const [exportMessage, setExportMessage] = useState(
    "No archive generated in this session.",
  );
  const networkTarget =
    exportTarget === "HF Hub" || exportTarget === "AuraOne Programs";
  const scope: ExportScope = {
    includeInterventions,
    includeSensorQa,
    includeEmbodimentCard,
  };
  const plannedArtifacts = artifactPlan(exportTarget, scope);

  function chooseTarget(target: ExportTarget) {
    setExportTarget(target);
    setExportState(
      target === "HF Hub" || target === "AuraOne Programs" ? "error" : "idle",
    );
    setExportProgress(0);
    setExportMessage(
      target === "HF Hub" || target === "AuraOne Programs"
        ? `${target} requires authentication and an upload adapter that are not configured.`
        : "Ready to generate a browser-downloaded ZIP archive.",
    );
  }

  async function startExport() {
    if (networkTarget || exportState === "running") {
      if (networkTarget) setExportState("error");
      return;
    }
    setExportState("running");
    setExportProgress(20);
    setExportMessage("Hashing selected artifacts...");
    await new Promise((resolve) => window.setTimeout(resolve, 120));
    setExportProgress(60);
    try {
      const archive = await buildLocalEvidenceArchive({
        manifest,
        dataset,
        episodes: filteredEpisodes,
        clusters: clusterRows,
        target: exportTarget,
        scope,
      });
      setExportProgress(90);
      downloadBinaryArtifact(archive.filename, archive.bytes, archive.mimeType);
      setExportProgress(100);
      setExportState("success");
      setExportMessage(
        `${archive.filename} downloaded with ${archive.entries.length} verified entries.`,
      );
      recordDiagnostic("robotics_export_completed", {
        target: exportTarget,
        episodes: filteredEpisodes.length,
        local_download: true,
        archive_entries: archive.entries.length,
      });
    } catch (error) {
      setExportState("error");
      setExportProgress(0);
      setExportMessage(
        error instanceof Error
          ? error.message
          : "The local ZIP archive could not be generated.",
      );
    }
  }

  function toggleStep(step: "source" | "destination" | "scope" | "artifacts") {
    setExpandedStep((current) => (current === step ? "artifacts" : step));
  }

  return (
    <section
      className="surface export-surface"
      aria-label="Export"
      tabIndex={0}
    >
      <div className="section-head">
        <div>
          <h1>Export</h1>
          <p>
            Confirm source, destination, scope, and real archive contents before
            generating a local evidence ZIP.
          </p>
        </div>
      </div>
      <DatasetProvenanceNote
        dataset={dataset}
        context="Archive contents are generated from the active filtered records."
      />
      <ol className="export-sequence">
        <li
          className={`export-step completed ${expandedStep === "source" ? "expanded" : ""}`}
          data-export-step="source"
        >
          <span className="step-number">1</span>
          <div className="step-content">
            <button
              className="step-toggle"
              aria-expanded={expandedStep === "source"}
              onClick={() => toggleStep("source")}
            >
              <span>
                <strong>Source</strong>
                <small>
                  {dataset.name} · {dataset.episodes.length} episodes
                </small>
              </span>
              <ChevronDown size={16} />
            </button>
            <div className="step-detail">
              <ViewContext
                dataset={dataset}
                summary={summary}
                label="Source dataset"
              />
            </div>
          </div>
        </li>
        <li
          className={`export-step completed ${expandedStep === "destination" ? "expanded" : ""}`}
          data-export-step="destination"
        >
          <span className="step-number">2</span>
          <div className="step-content">
            <button
              className="step-toggle"
              aria-expanded={expandedStep === "destination"}
              onClick={() => toggleStep("destination")}
            >
              <span>
                <strong>Destination</strong>
                <small>
                  {exportTarget}
                  {networkTarget ? " · not configured" : " · local download"}
                </small>
              </span>
              <ChevronDown size={16} />
            </button>
            <div className="step-detail">
              <div className="target-list">
                {exportTargets.map((target) => (
                  <button
                    key={target}
                    className={exportTarget === target ? "active" : ""}
                    onClick={() => chooseTarget(target)}
                  >
                    {target === "HF Hub" ? (
                      <UploadCloud size={16} />
                    ) : target === "Local disk" ? (
                      <HardDrive size={16} />
                    ) : target === "AuraOne Programs" ? (
                      <Send size={16} />
                    ) : (
                      <BookOpen size={16} />
                    )}
                    <span>{target}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </li>
        <li
          className={`export-step completed ${expandedStep === "scope" ? "expanded" : ""}`}
          data-export-step="scope"
        >
          <span className="step-number">3</span>
          <div className="step-content">
            <button
              className="step-toggle"
              aria-expanded={expandedStep === "scope"}
              onClick={() => toggleStep("scope")}
            >
              <span>
                <strong>Scope</strong>
                <small>
                  {filteredEpisodes.length} filtered records · metadata
                  artifacts only
                </small>
              </span>
              <ChevronDown size={16} />
            </button>
            <div className="step-detail">
              <div className="export-scope">
                <label>
                  <input type="checkbox" checked readOnly /> Reviewed filtered
                  subset <strong>{filteredEpisodes.length} episodes</strong>
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={includeInterventions}
                    onChange={(event) =>
                      setIncludeInterventions(event.target.checked)
                    }
                  />{" "}
                  Include intervention and anomaly records
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={includeSensorQa}
                    onChange={(event) =>
                      setIncludeSensorQa(event.target.checked)
                    }
                  />{" "}
                  Include sensor QA evidence
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={includeEmbodimentCard}
                    onChange={(event) =>
                      setIncludeEmbodimentCard(event.target.checked)
                    }
                  />{" "}
                  Include embodiment card
                </label>
                <p className="scope-boundary">
                  Raw media and selected local paths are never included by this
                  source build.
                </p>
              </div>
            </div>
          </div>
        </li>
        <li
          className={`export-step ${expandedStep === "artifacts" ? "expanded" : ""}`}
          data-export-step="artifacts"
        >
          <span className="step-number">4</span>
          <div className="step-content">
            <button
              className="step-toggle"
              aria-expanded={expandedStep === "artifacts"}
              onClick={() => toggleStep("artifacts")}
            >
              <span>
                <strong>Artifacts</strong>
                <small>
                  {plannedArtifacts.length} deterministic file
                  {plannedArtifacts.length === 1 ? "" : "s"}
                </small>
              </span>
              <ChevronDown size={16} />
            </button>
            <div className="step-detail">
              <div
                className="artifact-grid"
                role="region"
                aria-label="Generated export artifacts"
                tabIndex={0}
              >
                {plannedArtifacts.length ? (
                  plannedArtifacts.map((artifact) => (
                    <article key={artifact.name}>
                      {artifact.kind === "card" ? (
                        <BookOpen size={18} />
                      ) : artifact.kind === "qa" ? (
                        <ShieldCheck size={18} />
                      ) : artifact.kind === "checksum" ? (
                        <BadgeCheck size={18} />
                      ) : (
                        <FileJson size={18} />
                      )}
                      <strong>{artifact.name}</strong>
                      <span>{artifact.description}</span>
                    </article>
                  ))
                ) : (
                  <p>
                    No local artifacts are available for this network
                    destination.
                  </p>
                )}
              </div>
            </div>
          </div>
        </li>
      </ol>
      <section className="export-confirmation" data-export-step="confirmation">
        <div>
          <span className="eyebrow">Ready to confirm</span>
          <h2>{exportTarget}</h2>
          <p>
            {filteredEpisodes.length} filtered records from {dataset.name};{" "}
            {plannedArtifacts.length} archive entries are currently selected.
          </p>
          {networkTarget ? (
            <div className="handoff-disclosure">
              <ProoflineStatus
                tone="blocked"
                label="Network destination not configured"
              />
              <p>
                This source build can prepare a local manifest but cannot
                authenticate or upload to {exportTarget}. Choose a local
                destination to download evidence.
              </p>
            </div>
          ) : (
            <ProoflineStatus tone="info" label="Browser-downloaded local ZIP" />
          )}
          {exportState === "error" ? (
            <ProoflineStatus
              tone="danger"
              label={
                networkTarget
                  ? "Export blocked: destination is not configured"
                  : "Local download could not be generated"
              }
            />
          ) : null}
          {exportState === "success" ? (
            <ProoflineStatus
              tone="success"
              label="Local evidence ZIP downloaded"
            />
          ) : null}
          {exportState === "running" ? (
            <progress
              className="operation-progress"
              value={exportProgress}
              max={100}
              aria-label="Export progress"
            />
          ) : null}
          {exportState !== "idle" ? (
            <p className="export-state-message" role="status">
              {exportMessage}
            </p>
          ) : null}
        </div>
        <button
          className="primary export-cta"
          onClick={startExport}
          disabled={networkTarget || exportState === "running"}
          title={
            networkTarget
              ? `${exportTarget} authentication and upload are not configured in this source build.`
              : "Generate and download a deterministic local ZIP archive"
          }
        >
          {exportState === "running" ? (
            <Loader2 className="spin" size={16} />
          ) : (
            <Download size={16} />
          )}
          {exportState === "running"
            ? `Preparing ${exportProgress}%`
            : exportState === "success"
              ? "Download ZIP again"
              : "Confirm and export ZIP"}
        </button>
      </section>
      <details className="manifest raw-manifest">
        <summary>Raw manifest preview</summary>
        <pre role="region" tabIndex={0} aria-label="Raw export manifest">
          {JSON.stringify(manifest, null, 2)}
        </pre>
      </details>
    </section>
  );
}


export function CommandPalette({
  query,
  setQuery,
  close,
  executeCommand,
}: {
  query: string;
  setQuery: (query: string) => void;
  close: () => void;
  executeCommand: (commandId: CommandId) => void;
}) {
  const dialogRef = useDialogFocusTrap(close);
  const results = commands.filter((command) =>
    `${command.title} ${command.group} ${command.key}`
      .toLowerCase()
      .includes(query.toLowerCase()),
  );
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={close}>
      <section
        ref={dialogRef}
        className="command-palette"
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="command-search">
          <Search size={18} />
          <input
            data-autofocus
            aria-label="Search commands"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search every command"
          />
        </div>
        <div className="command-results">
          {results.map((command) => (
            <button
              key={command.id}
              type="button"
              onClick={() => executeCommand(command.id)}
            >
              <span>
                <strong>{command.title}</strong>
                <small>{command.group}</small>
              </span>
              <kbd>{command.key}</kbd>
            </button>
          ))}
          {!results.length ? (
            <p className="command-empty">No matching local command.</p>
          ) : null}
        </div>
      </section>
    </div>
  );
}


export function Onboarding({
  close,
  openSample,
  openLocal,
}: {
  close: () => void;
  openSample: () => void;
  openLocal: () => void;
}) {
  const dialogRef = useDialogFocusTrap(close);

  function chooseLocalDataset() {
    close();
    window.setTimeout(openLocal, 0);
  }

  return (
    <div className="modal-backdrop onboarding-backdrop" role="presentation">
      <section
        ref={dialogRef}
        className="onboarding"
        role="dialog"
        aria-modal="true"
        aria-label="First-run onboarding"
      >
        <div className="onboarding-grid">
          <div className="onboarding-copy">
            <RoboticsStudioLogo size="large" />
            <span className="eyebrow">Robotics Studio Open v{appVersion}</span>
            <h1>Start a local robot evidence review.</h1>
            <p>
              No AuraOne account is required. Your dataset remains on this
              machine unless you choose an explicit network export.
            </p>
            <ol className="first-run-steps">
              <li>
                <strong>Choose metadata</strong>
                <span>
                  Open JSON or JSONL episode metadata, or use the bundled
                  synthetic SO-101 fixture.
                </span>
              </li>
              <li>
                <strong>Confirm provenance</strong>
                <span>
                  Imported fields stay distinct from repository fixture
                  evidence.
                </span>
              </li>
              <li>
                <strong>Review evidence</strong>
                <span>
                  Inspect declared sensors, events, failures, and QA without
                  filling unknown fields.
                </span>
              </li>
              <li>
                <strong>Export locally</strong>
                <span>
                  Generate a checksummed ZIP containing only the selected
                  metadata artifacts.
                </span>
              </li>
            </ol>
            <div className="onboarding-actions">
              <button data-autofocus className="primary" onClick={openSample}>
                <Play size={15} />
                Explore sample
              </button>
              <button className="ghost" onClick={chooseLocalDataset}>
                Open my dataset
              </button>
              <button className="ghost" onClick={close}>
                Dismiss
              </button>
            </div>
            <dl
              className="onboarding-facts"
              role="group"
              aria-label="What this build does"
            >
              <div>
                <dt>Intake</dt>
                <dd>JSON manifests and JSONL episode records</dd>
              </div>
              <div>
                <dt>Evidence</dt>
                <dd>Only fields declared by the selected metadata</dd>
              </div>
              <div>
                <dt>Export</dt>
                <dd>Local ZIP, embodiment card, or failure gallery</dd>
              </div>
            </dl>
          </div>
          <aside
            className="onboarding-preview"
            aria-label="Sample review preview"
          >
            <div className="preview-topline">
              <span className="preview-name">
                <CheckCircle2 size={15} aria-hidden="true" />
                so101_kitchen_v3
              </span>
              <Chip label="96 visible" tone="pass" />
            </div>
            <div className="preview-media" aria-hidden="true">
              <span className="preview-media-main" />
              <span className="preview-media-sub" />
              <span className="preview-media-sub" />
            </div>
            <div className="preview-timeline" aria-hidden="true">
              <i style={{ left: "6%", width: "16%" }} />
              <i style={{ left: "26%", width: "20%" }} />
              <i style={{ left: "52%", width: "24%" }} />
              <i style={{ left: "82%", width: "12%" }} />
              <b style={{ left: "44%" }} />
            </div>
            <div className="preview-stats">
              <article>
                <span>Records</span>
                <strong>96</strong>
              </article>
              <article>
                <span>Seed scenes</span>
                <strong>3</strong>
              </article>
              <article>
                <span>Control rate</span>
                <strong>30 Hz</strong>
              </article>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}


export function ViewBody(props: {
  view: ViewMode;
  density: Density;
  setDensity: (density: Density) => void;
  dataset: DatasetTab;
  selectedEpisode: Episode;
  filteredEpisodes: Episode[];
  visibleGrid: Episode[];
  gridOffset: number;
  setGridOffset: (offset: number) => void;
  cursor: number;
  setCursor: (cursor: number | ((current: number) => number)) => void;
  playing: boolean;
  setPlaying: (playing: boolean | ((current: boolean) => boolean)) => void;
  speed: number;
  setSpeed: (speed: number) => void;
  chooseEpisode: (episode: Episode) => void;
  updateEpisode: (episodeId: string, partial: Partial<Episode>) => void;
  exportTarget: ExportTarget;
  setExportTarget: (target: ExportTarget) => void;
  manifest: ReturnType<typeof exportManifest>;
  diagnosticBufferEnabled: boolean;
  setDiagnosticBufferEnabled: (value: boolean) => void;
  diagnosticEntries: LocalDiagnosticEntry[];
  mediaDark: boolean;
  setMediaDark: (value: boolean) => void;
  networkOnline: boolean;
  updateState: UpdateState;
  setUpdateState: (state: UpdateState) => void;
  activeSensorId: string;
  setActiveSensorId: (sensorId: string) => void;
  clusterRows: Cluster[];
  clusterRunState: OperationState;
  clusterProgress: number;
  runClustering: () => void;
  splitCluster: (clusterId: string) => void;
  mergeCluster: (clusterId: string) => void;
  recordDiagnostic: (
    eventName:
      | "robotics_dataset_opened"
      | "robotics_export_completed"
      | "robotics_feature_used",
    payload: Record<string, string | number | boolean>,
  ) => void;
}) {
  if (props.view === "browse") return <BrowseView {...props} />;
  if (props.view === "scrub") return <ScrubView {...props} />;
  if (props.view === "clusters") return <ClusterView {...props} />;
  if (props.view === "compare")
    return (
      <CompareView
        dataset={props.dataset}
        selectedEpisode={props.selectedEpisode}
        peers={props.filteredEpisodes.slice(1, 4)}
      />
    );
  if (props.view === "probe") return <ProbeView dataset={props.dataset} />;
  if (props.view === "qa") return <QaView dataset={props.dataset} />;
  if (props.view === "settings") return <SettingsView {...props} />;
  return <ExportView {...props} />;
}
